import React from "react";
import type { HistoryRow } from "../common/Row";
import SbuxStyledTableRow from "../../../../components/SbuxTable/SbuxStyledTableRow";
import SbuxStyledTableCell from "../../../../components/SbuxTable/SbuxStyledTableCell";
import SbuxContentCopy from "../../../../components/SbuxContentCopy";
import {
  isPhoneNumber,
  phoneNumberDetail,
} from "../../../../utils/phoneNumber";
import { ellipse } from "../../../../utils/stringUtil";
import { getHistoryUpdatedTimezone } from "../../utils";
import { DocumentLines } from "@dea-web/icons";
import styles from "../styles";
import SbuxContentButton from "../../../../components/SbuxContentButton";
import { COLORS } from "../../../../constants";
import useCss from "../../../../hooks/useCss";
import { useAppSelector } from "../../../../store/types";
import { getTimezoneSelector } from "../../../../selectors/settingsSelector";

interface MasterRowProps {
  row: HistoryRow;
  toggleDialogOpen: () => void;
  transcriptBtnClickEvent: (copyTranscript: boolean) => void;
  linkRenderer: (value: string) => React.ReactNode;
  areTranscriptsEnabledForInstance: boolean;
  showTranscriptCopiedSnackbar?: boolean;
}

const MasterRow = ({
  row,
  toggleDialogOpen,
  transcriptBtnClickEvent,
  linkRenderer,
  areTranscriptsEnabledForInstance,
}: MasterRowProps) => {
  const classes = useCss(styles);
  const timezone = useAppSelector(getTimezoneSelector);

  return (
    <SbuxStyledTableRow sx={{ "& > *": { borderBottom: "unset" } }}>
      <SbuxStyledTableCell
        onClick={toggleDialogOpen}
        className={classes.cellFocus}
      >
        {getHistoryUpdatedTimezone(row, timezone)}
      </SbuxStyledTableCell>
      <SbuxStyledTableCell
        onClick={toggleDialogOpen}
        className={classes.cellFocus}
      >
        {String(row["Method"] ?? "")}
      </SbuxStyledTableCell>
      <SbuxStyledTableCell
        onClick={toggleDialogOpen}
        className={classes.cellFocus}
      >
        {ellipse(
          String(row["Queue"] ?? ""),
          process.env.REACT_APP_COLUMN_LENGTH_BEFORE_ELLIPSIS
            ? parseInt(process.env.REACT_APP_COLUMN_LENGTH_BEFORE_ELLIPSIS, 10)
            : undefined,
        )}
      </SbuxStyledTableCell>
      <SbuxStyledTableCell
        onClick={toggleDialogOpen}
        className={classes.cellFocus}
      >
        {ellipse(
          String(row["Ticket or Case #"] ?? ""),
          process.env.REACT_APP_COLUMN_LENGTH_BEFORE_ELLIPSIS
            ? parseInt(process.env.REACT_APP_COLUMN_LENGTH_BEFORE_ELLIPSIS, 10)
            : undefined,
        )}
      </SbuxStyledTableCell>
      <SbuxStyledTableCell>
        {isPhoneNumber(String(row["Caller ID"] ?? "")) ? (
          <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
            {linkRenderer(String(row["Caller ID"] ?? ""))}
            <SbuxContentCopy
              content={
                phoneNumberDetail(String(row["Caller ID"] ?? ""))
                  ?.phoneNumber ?? ""
              }
            />
          </div>
        ) : (
          String(row["Caller ID"] ?? "")
        )}
      </SbuxStyledTableCell>
      <SbuxStyledTableCell>
        {isPhoneNumber(String(row["Dialed #"] ?? "")) ? (
          <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
            {linkRenderer(String(row["Dialed #"] ?? ""))}
            <SbuxContentCopy
              content={
                phoneNumberDetail(String(row["Dialed #"] ?? ""))?.phoneNumber ??
                ""
              }
            />
          </div>
        ) : (
          String(row["Dialed #"] ?? "")
        )}
      </SbuxStyledTableCell>
      {areTranscriptsEnabledForInstance && (
        <SbuxStyledTableCell>
          {(row?.transcripts?.length ?? 0) >= 1 && (
            <SbuxContentButton
              disabled={!row?.areTranscriptsEnabledForCall}
              handleClick={() => transcriptBtnClickEvent(false)}
              iconName={DocumentLines}
              color={
                COLORS.PRIMARY as
                  | "primary"
                  | "secondary"
                  | "default"
                  | "inherit"
                  | "error"
                  | "info"
                  | "success"
                  | "warning"
              }
              iconSize="16px"
            />
          )}
          {(row?.transcripts?.length ?? 0) >= 1 && (
            <SbuxContentCopy
              disabled={!row?.areTranscriptsEnabledForCall}
              content={""}
              handleClick={() => transcriptBtnClickEvent(true)}
            />
          )}
        </SbuxStyledTableCell>
      )}
    </SbuxStyledTableRow>
  );
};

export default MasterRow;
